import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable, catchError, combineLatest, filter, map, of, shareReplay, switchMap, take, tap, withLatestFrom} from 'rxjs';
import {CompanyDetails} from 'src/app/dashboard/models/companyDetails.model';
import {HoldingAssign} from 'src/app/dashboard/models/holdingAssign.model';
import {RoutingService} from 'src/app/services/routing.service';
import {CompanyMsService} from 'src/app/services/shared/company-ms.service';
import {RadioBtn} from 'src/app/shared/components/form/radio-buttons/radio-buttons.component';
import AccountType from 'src/app/shared/enums/AccountType.enum';
import {BusinessEntityType} from 'src/app/shared/enums/BusinessEntityType.enum';
import {UnitApplicationParentType} from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import {DialogWithFormBase} from 'src/app/shared/types/DialogWithFormBase';
import {BankAccountsMsService} from '../../bank-accounts-ms.service';
import {EntityType} from '../entity-type.enum';
import {Dropdown} from 'primeng/dropdown';
import {CreateEditCompanyComponent} from 'src/app/company/create-edit-company/create-edit-company.component';
import {DialogService} from 'src/app/services/dialog.service';
import {UnitApplicationMsServiceService} from '../../cre/unit-application-ms-service.service';
import {UnitCreateBankAccountService} from 'src/app/shared/components/unit/unit-create-bank-account/unit-create-bank-account.service';
import {BaseResponseDto} from 'src/app/shared/models/BaseResponseDto.model';
import {UtilsService} from 'src/app/services/utils.service';
import {UnitApplicationDto} from '../../models/unitApplicationDto.model';
import {UserService} from 'src/app/services/shared/user.service';
import UnitAccountProvider from 'src/app/shared/enums/UnitAccountProvider.enum';
import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import {untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {BankAccountService} from 'src/app/services/shared/bank-account.service';

@Component({
  selector: 'terra-new-business-account-dialog',
  templateUrl: './new-business-account-dialog.component.html',
  styleUrls: ['./new-business-account-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BankAccountsMsService, UnitCreateBankAccountService, BankAccountService],
})
export class NewBusinessAccountDialogComponent extends DialogWithFormBase implements OnInit {
  pageForm: UntypedFormGroup;
  EntityType = EntityType;
  AccountType = AccountType;
  actionButtonClicked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  existingApplicationForEntity$: BehaviorSubject<UnitApplicationDto> = new BehaviorSubject<UnitApplicationDto>(null);

  accountProvider$ = this.userService.accountDetails$.pipe(
    map(user => {
      return user?.organizationDetails?.unitAccountProvider || UnitAccountProvider.Thread;
    })
  );

  bankingProviderString$ = this.accountProvider$.pipe(
    switchMap(orgAccountProvider => {
      const bankingProvider = orgAccountProvider == null ? UnitAccountProvider.Thread : orgAccountProvider;
      return of(UnitAccountProvider.toProvidedBy(bankingProvider));
    })
  );


  entityTypes = [
    {
      label: "Holding",
      value: EntityType.Holding,
      haveAccountType: true,
      isDefault: true,
      selectingEntityPlaceholderName: "holding",
      selectingEntityPlaceholderlabel: this.isHoldingIdInitiated() ? "Holding" : "Select holding",
      selectingEntityFormControlName: "holding",
      emptyStateText: "No holdings added yet.",
      selectingEntitySource: this.bankAccountsMsService.getHoldingForAssignAccount().pipe(shareReplay(1)),
      shouldHaveAddFooter: false,
      itemLabelBuilder: (entity: HoldingAssign) => entity.name
    },
    {
      label: "GP Level",
      value: EntityType.GpLevel,
      haveAccountType: false,
      isDefault: false,
      selectingEntityPlaceholderName: "business entity",
      selectingEntityPlaceholderlabel: "Business entity",
      selectingEntityFormControlName: "companyDetailsId",
      emptyStateText: "No business entities added yet.",
      selectingEntitySource: this.companyMsService.companies$.pipe(shareReplay(1)),
      shouldHaveAddFooter: true,
      itemLabelBuilder: (entity: CompanyDetails) => `${entity.name} ${BusinessEntityType.toString(entity.entityType) || ""}`
    }
  ];

  public get selectedEntityType(): {
    label: string;
    value: EntityType;
    haveAccountType: boolean;
    isDefault: boolean;
    selectingEntityPlaceholderName: string;
    selectingEntityPlaceholderlabel: string;
    selectingEntityFormControlName: string;
    emptyStateText: string;
    selectingEntitySource: Observable<any>;
    shouldHaveAddFooter: boolean;
    itemLabelBuilder: (entity: any) => string;
  } {
    return this.entityTypes.find(type => type.value === this.pageForm.get('entityType')?.value)
  }

  entityTypesButtons: RadioBtn[] = this.entityTypes.map(type => ({
    value: type.value,
    text: type.label,
    checked: false,
    disabled: type.value === EntityType.GpLevel && this.isHoldingIdInitiated()
  } as RadioBtn))

  accountTypes = AccountType.listAll();

  constructor(private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<NewBusinessAccountDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private routingService: RoutingService,
    private companyMsService: CompanyMsService,
    private bankAccountsMsService: BankAccountsMsService,
    private dialogService: DialogService,
    private cdf: ChangeDetectorRef,
    private unitApplicationMsService: UnitApplicationMsServiceService,
    private unitCreateBankAccountService: UnitCreateBankAccountService,
    private utilsService: UtilsService,
    private userService: UserService,
    private bankAccountService: BankAccountService
  ) {
    super();
  }

  ngOnInit(): void {
    this.generateForm();
    this.pageForm.get('entityType').valueChanges.pipe(untilComponentDestroyed(this), tap(_ => this.changeValidatorsAccordingToSelectedEntityType())).subscribe();
    combineLatest(this.entityTypes.map(entityType => this.pageForm.get(entityType.selectingEntityFormControlName).valueChanges.pipe(map(val => ({entityType, val}))))).pipe(
      map(values => values.filter(val => val.entityType.value === this.selectedEntityType.value)?.[0]?.val?.toString()),
      filter(value => !!value),
      switchMap(value => this.unitApplicationMsService.getUnitApplicationsByEntityId(value)),
      tap(applications => this.existingApplicationForEntity$.next(applications?.[0]))
    ).subscribe();
    this.changeValidatorsAccordingToSelectedEntityType();
  }

  changeValidatorsAccordingToSelectedEntityType(): void {
    const selectedEntityType = this.selectedEntityType;
    this.pageForm.get(selectedEntityType.selectingEntityFormControlName).setValidators(Validators.required);
    this.pageForm.get(selectedEntityType.selectingEntityFormControlName).updateValueAndValidity()
    const nonSelected = this.entityTypes.filter(entityType => entityType.value != selectedEntityType.value);
    nonSelected.forEach(nonSelectedEntityType => {
      this.pageForm.get(nonSelectedEntityType.selectingEntityFormControlName).clearValidators();
      this.pageForm.get(nonSelectedEntityType.selectingEntityFormControlName).updateValueAndValidity()
    });
  }

  
  isHoldingIdInitiated(){
    return this.data !== null && this.data !== undefined && this.data.holdingId !== null && this.data.holdingId !== undefined
  }

  generateForm() {
    this.pageForm = this.fb.group({
      nickname: [null, Validators.required],
      entityType: [(this.entityTypes?.filter(type => type.isDefault)?.[0] || this.entityTypes?.[0])?.value, Validators.required],
      holding: this.isHoldingIdInitiated() ? this.data.holdingId : null,
      accountType: AccountType.Primary,
      companyDetailsId: null
    });
    if (this.isHoldingIdInitiated()) {
      this.pageForm.get('holding').disable({ emitEvent: false });
    }
  }

  createBankAccount(applicationId: number) {
    this.unitCreateBankAccountService.updateBankAccountFormDetails({
      accountNickname: this.pageForm.get('nickname').value,
      accountType: this.pageForm.get('accountType')?.getRawValue(),
      holding: this.selectedEntityType.value == EntityType.GpLevel ? null : {id: this.pageForm.get('holding').value} as GpHoldingListItemReqRes,
      unitApplicationId: applicationId,
      preselectedType: this.selectedEntityType.value == EntityType.GpLevel ? UnitApplicationParentType.Organization : UnitApplicationParentType.Holding
    });
    this.unitCreateBankAccountService.createBankAccount().pipe(
      tap(response => {
        this.dialogRef.close();
        this.router.navigateByUrl(this.routingService.creBankAccountPage(response.id));
      }),
      catchError(error => of(error instanceof BaseResponseDto ? this.utilsService.alertErrorMessage(error) : console.error(error)))
    )
      .subscribe();
  }

  createApplication() {
    this.dialogRef.close();

    // build the query params for the routing (base params are the common params for all cases)
    const baseParams = {
      bankAccountName: '',
      accountNickname: this.pageForm.get('nickname')?.value,
    };
    
    const entityParams =
      this.selectedEntityType.value === EntityType.GpLevel
        ? {
            type: UnitApplicationParentType.Organization,
            companyId: this.pageForm.get('companyDetailsId')?.value,
          }
        : {
            holdingId: this.pageForm.get('holding')?.value,
            accountTypeId: this.pageForm.get('accountType').getRawValue(),
            type: UnitApplicationParentType.Holding,
          };

    this.router.navigate(
      [this.routingService.createBankApplicationPage()],
      { queryParams: { ...baseParams, ...entityParams } }
    );
  }

  getSelectEntityPlaceholder() {
    return `Select ${this.selectedEntityType?.selectingEntityPlaceholderName}`
  }

  @ViewChild('businessTypeSelect') businessTypeSelect: Dropdown
  createEditCompany(companyDetails: CompanyDetails) {
    this.dialogService.customDialog<CreateEditCompanyComponent>(CreateEditCompanyComponent, companyDetails, true, 'create-edit-company').afterClosed().pipe(
      take(1),
      filter(company => !!company)
    ).subscribe(company => {
      this.pageForm.get('companyDetailsId').setValue(company.id);
      this.pageForm.get('companyDetailsId').updateValueAndValidity();
      this.businessTypeSelect.writeValue(company.id);
      this.cdf.detectChanges();
    })
  }

  actionButtonClicked() {
    this.pageForm.markAllAsTouched();
    this.pageForm.updateValueAndValidity();
    if (this.pageForm.valid) {
      this.existingApplicationForEntity$.
        pipe(take(1), tap(application => !!application ? this.createBankAccount(application.id) : this.createApplication())).
        subscribe();
    }
  }
  FDICInfo() {
    this.bankAccountService.FDICInfo();
  }
}
